'use client';

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Plus, Edit3, Trash2, Star, Loader2, MoreVertical, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAddresses } from '@/hooks/useAddresses';
import { EmptyState } from './EmptyState';
import { Button } from './ui/Button';
import { Address } from '@/lib/Apis';
import { AddressFormModal } from './AddressFormModal';
import { useNotification } from '@/providers/NotificationProvider';
import { ConfirmModal } from './ui/ConfirmModal';

export function AccountAddressesTab() {
    const { addresses, isLoading, error, removeAddress, markAsDefault, refresh } = useAddresses();

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
    const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const notification = useNotification();

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDelete = async (address: Address) => {
        if (!confirm(`Remove ${address.name}'s address?\nYou can add it back at any time.`)) return;

        setDeletingId(address.id);
        try {
            await removeAddress(address.id);
        } catch (error) {
            console.error('Failed to delete address:', error);
            alert('Failed to delete address. Please try again.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleSetDefault = async (address: Address) => {
        try {
            await markAsDefault(address.id);
        } catch (error) {
            console.error('Failed to set default address:', error);
            alert('Failed to set default address. Please try again.');
        }
    };

    const handleConfirmDelete = async () => {
        if (!addressToDelete) return;

        setDeletingId(addressToDelete.id);
        try {
            await removeAddress(addressToDelete.id);
            if (selectedAddressId === addressToDelete.id) {
                setSelectedAddressId(null);
            }
            notification.success('Address deleted successfully', addressToDelete.name + ' address deleted successfully');
        } catch (error) {
            console.error('Failed to delete address:', error);
            notification.error('Failed to delete address. Please try again.', 'Failed to delete address' + addressToDelete.name);
        } finally {
            setDeletingId(null);
            setAddressToDelete(null);
        }
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                    {Array.from({ length: 2 }).map((_, index) => (
                        <div
                            key={index}
                            className="h-48 animate-pulse rounded-lg border border-slate-100 bg-slate-50"
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="rounded-lg border border-red-100 bg-red-50 p-6 text-sm text-red-700">
                    {error.message}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="rounded-xl">
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-3xl font-bold text-hsecondary">Saved Addresses</h2>
                        <p className="text-sm text-hmuted mt-1">Manage your delivery and billing addresses</p>
                    </div>
                    {addresses.length > 0 && (
                        <button
                            className='flex w-full sm:w-auto flex-row items-center justify-center gap-2 px-6 py-2 border border-slate-200 rounded-xl text-slate-700 hover:opacity-80 transition-colors bg-hsecondary text-white text-sm'
                            onClick={() => {
                                setEditingAddress(undefined);
                                setIsAddressModalOpen(true);
                            }}
                        >
                            <Plus className="h-4 w-4" />
                            Add Address
                        </button>
                    )}
                </div>

                {addresses.length === 0 ? (
                    <EmptyState
                        icon={MapPin}
                        title="No addresses yet"
                        description="Save a shipping or billing address to speed through checkout."
                        actionLabel="Add your first address"
                        onAction={() => {
                            setEditingAddress(undefined);
                            setIsAddressModalOpen(true);
                        }}
                    />
                ) : (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
                        {addresses.map((address) => (
                            <motion.div
                                key={address.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                                className={`
                                    group relative rounded-lg border bg-white
                                    transition-all duration-200
                                    ${address.isDefault
                                        ? 'border-slate-300 shadow-sm ring-1 ring-slate-900/5'
                                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                    }
                                `}
                            >
                                {/* Card Content */}
                                <div className="p-6">
                                    {/* Header */}
                                    <div className="flex items-start justify-between gap-4 mb-5">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-base font-semibold text-slate-900 truncate">
                                                    {address.name}
                                                </h3>
                                                {address.isDefault && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-xs font-medium text-slate-700 border border-slate-200">
                                                        <Star className="h-3 w-3 fill-slate-700" />
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-slate-500">Delivery address</p>
                                        </div>

                                        {/* Desktop Actions */}
                                        <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => {
                                                    setEditingAddress(address);
                                                    setIsAddressModalOpen(true);
                                                }}
                                                className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                                aria-label="Edit address"
                                                title="Edit"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                            </button>
                                            {!address.isDefault && (
                                                <button
                                                    onClick={() => handleSetDefault(address)}
                                                    className="p-2 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                    aria-label="Set as default"
                                                    title="Set as default"
                                                >
                                                    <Star className="h-4 w-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setAddressToDelete(address)}
                                                disabled={deletingId === address.id}
                                                className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                aria-label="Delete address"
                                                title="Delete"
                                            >
                                                {deletingId === address.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>

                                        {/* Mobile Menu */}
                                        <div className="sm:hidden" ref={activeMenuId === address.id ? menuRef : null}>
                                            <button
                                                onClick={() => setActiveMenuId(activeMenuId === address.id ? null : address.id)}
                                                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                                aria-label="More options"
                                            >
                                                <MoreVertical className="h-5 w-5" />
                                            </button>

                                            <AnimatePresence>
                                                {activeMenuId === address.id && (
                                                    <motion.div
                                                        initial={{ opacity: 0, scale: 0.96, y: -8 }}
                                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.96, y: -8 }}
                                                        transition={{ duration: 0.12 }}
                                                        className="absolute right-6 top-16 w-48 rounded-lg border border-slate-200 bg-white shadow-lg z-50 overflow-hidden"
                                                    >
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingAddress(address);
                                                                    setIsAddressModalOpen(true);
                                                                    setActiveMenuId(null);
                                                                }}
                                                                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                                            >
                                                                <Edit3 className="h-4 w-4 text-slate-400" />
                                                                Edit
                                                            </button>
                                                            {!address.isDefault && (
                                                                <button
                                                                    onClick={() => {
                                                                        handleSetDefault(address);
                                                                        setActiveMenuId(null);
                                                                    }}
                                                                    className="flex w-full items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                                                >
                                                                    <Star className="h-4 w-4 text-slate-400" />
                                                                    Set as default
                                                                </button>
                                                            )}
                                                            <div className="my-1 border-t border-slate-100" />
                                                            <button
                                                                onClick={() => {
                                                                    setAddressToDelete(address);
                                                                    setActiveMenuId(null);
                                                                }}
                                                                disabled={deletingId === address.id}
                                                                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                                            >
                                                                {deletingId === address.id ? (
                                                                    <>
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                        Deleting...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Trash2 className="h-4 w-4" />
                                                                        Delete
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>

                                    {/* Address Details */}
                                    <div className="space-y-3">
                                        <div className="flex gap-3">
                                            <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-slate-700 leading-relaxed">
                                                    {address.street1}
                                                    {address.street2 && <>, {address.street2}</>}
                                                </p>
                                                <p className="text-sm text-slate-600 mt-0.5">
                                                    {address.city}, {address.state} {address.zip}
                                                </p>
                                                <p className="text-sm text-slate-500 mt-0.5">
                                                    {address.country}
                                                </p>
                                            </div>
                                        </div>

                                        {address.phone && (
                                            <div className="flex gap-3">
                                                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                                <p className="text-sm text-slate-700">{address.phone}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
            <AddressFormModal
                isOpen={isAddressModalOpen}
                onClose={() => setIsAddressModalOpen(false)}
                initialAddress={editingAddress}
                onAddressAdded={() => {
                    refresh();
                }}
                onAddressUpdated={() => {
                    refresh();
                }}
            />

            <ConfirmModal
                isOpen={!!addressToDelete}
                onClose={() => setAddressToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Address"
                message={`Are you sure you want to delete ${addressToDelete?.name}'s address? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
                isLoading={deletingId === addressToDelete?.id}
            />
        </>
    );
}
